import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
} from '@tanstack/react-query';
import { ApiError, get, getWithAuth, post, put, patch, deleteRequest } from '../utils/apiClient';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse a Response to JSON, returning null on empty bodies. */
async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}

/** Extract a user-friendly message from an error response body. */
function extractErrorMessage(status: number, body: string, statusText: string): string {
  if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  // Try to extract detail/message from JSON error responses
  try {
    const parsed = JSON.parse(body);
    const detail = parsed.detail ?? parsed.message;
    if (typeof detail === 'string' && detail) return detail;
  } catch { /* body is not JSON, use as-is */ }
  return body || statusText;
}

/**
 * React Query retry predicate that treats non-retryable HTTP responses as
 * terminal. 4xx client errors (403 forbidden, 404 not found, validation
 * errors) will never be made successful by retrying, so retrying them just
 * produces extra load and console noise. 429 is also excluded here because
 * ``fetchWithAuth`` already handles rate-limit backoff at the transport
 * layer; React Query retrying on top of that amplifies the storm.
 *
 * Network-level failures and 5xx responses continue to retry up to twice
 * (React Query's default), since those can succeed on a second attempt.
 */
function shouldRetryQuery(failureCount: number, error: Error): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) {
    if (error.status >= 400 && error.status < 500) return false;
  }
  return true;
}

/** Fetch via apiClient.get and parse JSON. Throws ApiError on non-ok responses. */
async function fetchGet<T>(endpoint: string): Promise<T> {
  const res = await get(endpoint);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(
      extractErrorMessage(res.status, body, res.statusText),
      res.status,
      body,
    );
  }
  return parseJson<T>(res);
}

/** Same as fetchGet but uses getWithAuth (identical today, kept for symmetry). */
async function fetchGetAuth<T>(endpoint: string): Promise<T> {
  const res = await getWithAuth(endpoint);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(
      extractErrorMessage(res.status, body, res.statusText),
      res.status,
      body,
    );
  }
  return parseJson<T>(res);
}

// ---------------------------------------------------------------------------
// useApiGet – drop-in replacement for useEffect + get() + useState
// ---------------------------------------------------------------------------

/**
 * Fetches data from an API endpoint using React Query.
 *
 * @param queryKey  Unique cache key (array). Pass `null`/`undefined` to disable.
 * @param endpoint  API endpoint string passed to `apiClient.get`.
 * @param options   Extra React Query options (staleTime, enabled, etc.).
 *
 * @example
 *   const { data: patients, isLoading } = useApiGet<Patient[]>(
 *     ['patients'],
 *     '/api/patients',
 *   );
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiGet<T = any>(
  queryKey: QueryKey | null | undefined,
  endpoint: string,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'enabled'>,
) {
  const { queryFn: customQueryFn, retry: customRetry, ...rest } = options || {};
  return useQuery<T, Error>({
    queryKey: queryKey as QueryKey,
    queryFn: customQueryFn ?? (() => fetchGet<T>(endpoint)),
    enabled: !!queryKey,
    retry: customRetry ?? shouldRetryQuery,
    ...rest,
  });
}

/**
 * Same as useApiGet but uses getWithAuth explicitly.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiGetAuth<T = any>(
  queryKey: QueryKey | null | undefined,
  endpoint: string,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  const { retry: customRetry, ...rest } = options || {};
  return useQuery<T, Error>({
    queryKey: queryKey as QueryKey,
    queryFn: () => fetchGetAuth<T>(endpoint),
    enabled: !!queryKey,
    retry: customRetry ?? shouldRetryQuery,
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// useApiMutation – drop-in replacement for manual post/put/patch/delete
// ---------------------------------------------------------------------------

type MutationMethod = 'post' | 'put' | 'patch' | 'delete';

const methodMap = {
  post,
  put,
  patch,
  delete: deleteRequest,
} as const;

interface MutationVars {
  data?: unknown;
}

/**
 * Wraps a mutation (POST/PUT/PATCH/DELETE) with React Query.
 *
 * @param endpoint     API endpoint.
 * @param method       HTTP method.
 * @param invalidateKeys  Query keys to invalidate on success.
 * @param options      Extra React Query mutation options.
 *
 * @example
 *   const { mutateAsync: createCase } = useApiMutation(
 *     '/api/cases',
 *     'post',
 *     [['cases']],
 *   );
 */
export function useApiMutation<TResponse = unknown>(
  endpoint: string,
  method: MutationMethod = 'post',
  invalidateKeys?: QueryKey[],
  options?: Omit<UseMutationOptions<TResponse, Error, MutationVars>, 'mutationFn'>,
) {
  const qc = useQueryClient();
  const fn = methodMap[method];

  const { onSuccess: callerOnSuccess, ...restOptions } = options || {};

  return useMutation<TResponse, Error, MutationVars>({
    mutationFn: async (vars: MutationVars) => {
      const res = await (fn as typeof post)(endpoint, vars?.data);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`API ${res.status}: ${body || res.statusText}`);
      }
      return parseJson<TResponse>(res);
    },
    onSuccess: (...args) => {
      if (invalidateKeys) {
        invalidateKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      }
      callerOnSuccess?.(...args);
    },
    ...restOptions,
  });
}

// ---------------------------------------------------------------------------
// Paginated response helpers
// ---------------------------------------------------------------------------

/**
 * Default page size appended to list endpoints that should fetch all records
 * in a single call. Consumers use `?limit=${DEFAULT_PAGE_LIMIT}` to override
 * the backend default (50). Centralised here so the value is easy to change.
 */
export const DEFAULT_PAGE_LIMIT = 500;

/** Shape returned by paginated API endpoints. */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Unwrap a paginated envelope returned by list endpoints.
 *
 * The backend returns `{ items: <payload>, total, limit, offset }` where
 * `<payload>` can be:
 *   – a flat array (default / ungrouped)
 *   – a grouped dict  `{ owner_items: [...], your_items: [...] }`
 *   – (legacy) a bare array (pre-pagination backward compat)
 *
 * This helper normalises all shapes into a flat array.
 */
export function unwrapPaginatedItems<T = unknown>(raw: unknown): T[] {
  if (!raw) return [];
  // Legacy: bare array (pre-pagination response)
  if (Array.isArray(raw)) return raw as T[];
  // Paginated envelope
  const envelope = raw as Record<string, unknown>;
  const inner = envelope.items !== undefined ? envelope.items : raw;
  // Inner is a flat array
  if (Array.isArray(inner)) return inner as T[];
  // Inner is a grouped dict { owner_items, your_items }
  if (inner && typeof inner === 'object') {
    const grouped = inner as Record<string, unknown>;
    if (grouped.owner_items || grouped.your_items) {
      return [
        ...((grouped.owner_items || []) as T[]),
        ...((grouped.your_items || []) as T[]),
      ];
    }
  }
  return [];
}

/**
 * Infinite-scroll hook for paginated API endpoints that return
 * `{ items: T[], total: number, limit: number, offset: number }`.
 *
 * @param queryKey  Unique cache key (array). Pass `null`/`undefined` to disable.
 * @param endpoint  Base API endpoint (without limit/offset params).
 * @param pageSize  Number of items per page (default 50).
 *
 * @example
 *   const { data, fetchNextPage, hasNextPage, isLoading } = useApiPaginated<Patient>(
 *     ['patients'],
 *     '/api/patients',
 *     50,
 *   );
 *   const allItems = data?.pages.flatMap(p => p.items) ?? [];
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiPaginated<T = any>(
  queryKey: QueryKey | null | undefined,
  endpoint: string,
  pageSize = 50,
) {
  return useInfiniteQuery<PaginatedResponse<T>, Error>({
    queryKey: queryKey as QueryKey,
    queryFn: async ({ pageParam = 0 }) => {
      const separator = endpoint.includes('?') ? '&' : '?';
      const url = `${endpoint}${separator}limit=${pageSize}&offset=${pageParam}`;
      return fetchGet<PaginatedResponse<T>>(url);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: !!queryKey,
  });
}

// Re-export useQueryClient for convenience in pages that need manual invalidation
export { useQueryClient } from '@tanstack/react-query';
